import { useEffect, useMemo } from "react";
import { AlertTriangle, RefreshCw } from "@digital-twins/design-system/icons";
import type { StudyDesign } from "@digital-twins/shared-types";
import { DEFAULT_TARGET_BASE_PER_CUT } from "@digital-twins/shared-types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RespondentStepper } from "./RespondentStepper";
import { useTranslation } from "react-i18next";
import {
  clampPerGroup,
  groupCount,
  perGroupFromRespondents,
  perGroupLimits,
  respondentsFromPerGroup,
  sizingGroupRows,
  sizingSummary,
} from "../utils/perGroupSizing";

interface StudySizingSectionProps {
  /** Resolved by the questionnaire and the audience, never picked here. */
  design: StudyDesign;
  cutCount: number;
  /** Group names in engine cut order. */
  labels: Array<string>;
  numRespondents: number | null;
  setNumRespondents: (value: number | null) => void;
  minRespondents: number;
  maxRespondents: number;
  disabled?: boolean;
}

/**
 * Study sizing: one number per group, then what that number buys.
 *
 * The design is not a question we ask. A monadic questionnaire is monadic, and
 * whether each group gets its own twins or every twin rates every version falls
 * out of the audience size. This section only sizes the groups that shape
 * implies, and says plainly when the audience is what limits them.
 */
export function StudySizingSection({
  design,
  cutCount,
  labels,
  numRespondents,
  setNumRespondents,
  minRespondents,
  maxRespondents,
  disabled = false,
}: StudySizingSectionProps) {
  const { t } = useTranslation("app");
  const limits = useMemo(
    () =>
      perGroupLimits({ design, cutCount, minRespondents, maxRespondents }),
    [design, cutCount, minRespondents, maxRespondents],
  );

  const perGroup = clampPerGroup(
    perGroupFromRespondents(design, cutCount, numRespondents),
    limits,
  );
  const groups = groupCount(design, cutCount);
  const hasGroups = groups > 1;

  // A total that is not a whole number of groups (a restored draft, a cap that
  // divided unevenly) would show one number here and bill another, and the run
  // would size its groups off the stored total. Snap it to what is displayed.
  const normalized = respondentsFromPerGroup(design, cutCount, perGroup);
  useEffect(() => {
    if (numRespondents !== normalized) {
      setNumRespondents(normalized);
    }
  }, [normalized, numRespondents, setNumRespondents]);

  const rows = sizingGroupRows({ design, cutCount, perGroup, labels });
  const summary = sizingSummary({ design, cutCount, perGroup });
  const audienceIsTheLimit = limits.max < DEFAULT_TARGET_BASE_PER_CUT;

  return (
    <div data-testid="study-sizing">
      <RespondentStepper
        id="study-respondents"
        label={hasGroups ? t("creation.respondentsPerGroup") : t("creation.numberOfRespondents")}
        value={perGroup}
        limits={limits}
        onChange={(next) =>
          setNumRespondents(respondentsFromPerGroup(design, cutCount, next))
        }
        disabled={disabled}
        data-testid="study-sizing-per-group"
      />

      {hasGroups && (
        <div
          className="mt-3 rounded-xl border border-border bg-background overflow-hidden"
          data-testid="study-sizing-grid"
        >
          {rows.map((row, index) => (
            <div
              key={`${row.name}-${index}`}
              className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-border last:border-b-0"
            >
              <span className="text-sm text-foreground dark:text-white truncate">
                {row.name}
              </span>
              <span className="flex items-center gap-1.5 text-sm font-medium tabular-nums text-foreground dark:text-white">
                {row.respondents.toLocaleString()}
                {row.sharesTwins && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="text-muted-foreground"
                          aria-label={t("creation.twinsReuseAria")}
                          data-testid="study-sizing-reuse-badge"
                        >
                          <RefreshCw aria-hidden="true" className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[260px] text-xs">
                        {t("creation.twinsReuseHint")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 bg-card/60">
            {summary.sharesTwins ? (
              <>
                <span className="text-sm text-muted-foreground dark:text-[#BDBDBD] tabular-nums">
                  {t("creation.twinsCountShort", { count: summary.twins })}
                </span>
                <span
                  className="text-sm font-medium tabular-nums text-foreground dark:text-white"
                  data-testid="study-sizing-total"
                >
                  {summary.responses.toLocaleString()} responses
                </span>
              </>
            ) : (
              <>
                <span className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
                  {t("creation.twinsInTotal")}
                </span>
                <span
                  className="text-sm font-medium tabular-nums text-foreground dark:text-white"
                  data-testid="study-sizing-total"
                >
                  {summary.twins.toLocaleString()}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {audienceIsTheLimit && (
        <p
          className="mt-2.5 flex items-start gap-1.5 text-sm text-amber-600 dark:text-amber-400"
          data-testid="study-sizing-cap-note"
        >
          <AlertTriangle
            aria-hidden="true"
            className="h-3.5 w-3.5 mt-0.5 flex-shrink-0"
          />
          <span>
            {limits.max.toLocaleString()}{" "}
            {hasGroups ? "per group is" : "respondents is"} the most this
            audience can give.
          </span>
        </p>
      )}
    </div>
  );
}
